import { IFlow, ISO8601Date, OrNull } from "config";

/*
..................
~~~ INTERFACES ~~~
..................
*/

/* ------------------    DYNAMIC FORM    ------------------ */
export interface IDynamicForm {
  description: string;
  flow: Pick<IFlow, "slug"> | { slug: "" };
  id: string;
  items: DynamicFormItems[];
  layout: DynamicFormLayoutType;
  slug: string;
  expiration?: IDynamicFormExpiration;
  groups?: OrNull<DynamicFormGroup[]>;
  leadingNavigationButton?: ButtonFormItem["content"];
}

export interface IDynamicFormExpiration {
  actions: IButtonAction[];
  effectiveUntil: ISO8601Date;
}

export type DynamicFormGroup = ApiFormGroup | ComponentFormGroup;

export type DynamicFormItems =
  | ButtonFormItem
  | DecorationFormItem
  | CheckboxInputFormItem
  | DateInputFormItem
  | DirectUploadInputFormItem
  | ImageFormItem
  | ListRowFormItem
  | MultiSelectInputFormItem
  | NumericInputFormItem
  | SingleSelectInputFormItem
  | SliderInputFormItem
  | TaxIdInputFormItem
  | TextInputFormItem
  | ToggleInputFormItem
  | UnitizedInputFormItem
  | SpacerFormItem
  | TextFormItem;

export type DynamicFormInputItems = Exclude<
  DynamicFormItems,
  { type: keyof typeof EnumDynamicFormNonInputItemTypes }
>;

export interface IApiArgument {
  name: EnumRetryArgumentName | string;
  source: DynamicFormItem["key"] | string;
  sourceType: ArgumentSourceType;
  type: ArgumentType;
}

/* ------------------    FORM GROUP    ------------------ */
interface IDynamicFormGroup<T extends FormGroupType = FormGroupType> {
  detail: GroupDetailMap[T];
  type: FormGroupType;
}

export type ApiFormGroup = IDynamicFormGroup<"api">;

export type ComponentFormGroup = IDynamicFormGroup<"component">;

interface ApiGroupDetail {
  key: string;
  mutationBody: string;
  mutationName: string;
  mutationVariables: IApiArgument[];
}

export interface ContainerAttributes {
  backgroundColor?: ContentColorType;
  cornerRadius?: number;
  horizontalPadding?: number;
  margin?: number;
  shadow?: boolean;
  verticalPadding?: number;
}

type AttributesSetMap = {
  address: null;
  container: ContainerAttributes;
  phone: null;
  default: null;
};

interface ComponentGroupDetail {
  attributesSet: AttributesSetMap[GroupDetailComponentTypes];
  itemKeys: DynamicFormItem["key"][];
  replacementKey: DynamicFormItem["key"];
  type: GroupDetailComponentTypes;
}

type GroupDetailMap = {
  api: ApiGroupDetail;
  component: ComponentGroupDetail;
};

/* ------------------   FORM ITEMS   ------------------ */
interface DynamicFormItem<T extends DynamicFormItemType = DynamicFormItemType> {
  content: ItemContentMap[T];
  key: string;
  type: T;
}

export type ButtonFormItem = DynamicFormItem<"button">;
export type DecorationFormItem = DynamicFormItem<"decoration">;
export type CheckboxInputFormItem = DynamicFormItem<"inputCheckbox">;
export type DateInputFormItem = DynamicFormItem<"inputDate">;
export type DirectUploadInputFormItem = DynamicFormItem<"inputDirectUpload">;
export type ImageFormItem = DynamicFormItem<"image">;
export type ListRowFormItem = DynamicFormItem<"listRow">;
export type MultiSelectInputFormItem = DynamicFormItem<"inputMultiSelect">;
export type NumericInputFormItem = DynamicFormItem<"inputNumeric">;
export type SingleSelectInputFormItem = DynamicFormItem<"inputSingleSelect">;
export type SliderInputFormItem = DynamicFormItem<"inputSlider">;
export type TaxIdInputFormItem = DynamicFormItem<"inputTaxId">;
export type TextInputFormItem = DynamicFormItem<"inputText">;
export type ToggleInputFormItem = DynamicFormItem<"inputToggle">;
export type UnitizedInputFormItem = DynamicFormItem<"inputUnitized">;
export type TextFormItem = DynamicFormItem<"text">;
export type SpacerFormItem = DynamicFormItem<"space">;

/* ------------------   FORM ITEM - STATIC CONTENT    ------------------ */
type ItemContentMap = {
  button: IButtonContent;
  decoration: IDecorationContent;
  image: IImageContent;
  inputCheckbox: ICheckboxInputContent;
  inputDate: IDateInputContent;
  inputDirectUpload: IDirectUploadInputContent;
  inputMultiSelect: IMultiSelectInputContent;
  inputNumeric: INumericInputContent;
  inputSingleSelect: ISingleSelectInputContent;
  inputSlider: ISliderInputContent;
  inputTaxId: ITaxIdInputContent;
  inputText: ITextInputContent;
  inputToggle: IToggleInputContent;
  inputUnitized: IUnitizedInputContent;
  listRow: IListRowContent;
  text: ITextContent;
  space: ISpacerContent;
};

interface IButtonContent {
  actions: IButtonAction[];
  fixed: boolean;
  primary: boolean;
  style: ButtonStyleType;
  text: string;
}

export interface IButtonAction {
  command: IButtonCommand;
  type: ButtonActionType;
}

interface IButtonCommand {
  arguments?: OrNull<IApiArgument[]>;
  destination: string;
}

interface IDecorationContent {
  slug: DecorationKeyType | string;
}

interface IImageContent {
  alignment: "centered" | "leading" | "trailing";
  name: string;
  fillColor?: OrNull<string>;
  height?: OrNull<number>;
  width?: OrNull<number>;
}

export interface IListRowColumnContent {
  actions?: OrNull<IButtonAction[]>;
  image?: OrNull<IImageContent>;
  texts?: OrNull<ITextContent[]>;
}

interface IListRowContent {
  showsDivider: boolean;
  verticalPadding: SpacerValueType;
  actions?: OrNull<IButtonAction[]>;
  leadingColumn?: OrNull<IListRowColumnContent>;
  trailingColumn?: OrNull<IListRowColumnContent>;
}

interface ISpacerContent {
  min: SpacerValueType;
  mode: SpacerModeType;
}

interface ITextContent {
  color: ContentColorType;
  html: boolean;
  text: string;
  typography: TextContentTypographyType;
  backgroundColor?: ContentColorType;
  style?: TextContentStyleType;
}

/* ------------------    FORM ITEM - INPUT CONTENT    ------------------ */
interface InputContentBase {
  disabled: boolean;
  label: string;
  required: boolean;
  id?: string;
}

export interface ICheckboxInputContent extends InputContentBase {
  text: string;
  acknowledgementGroupKeys?: string[];
  value?: OrNull<boolean>;
}

export interface IDateInputContent extends InputContentBase {
  hint?: string;
  max?: OrNull<ISO8601Date>;
  min?: OrNull<ISO8601Date>;
  value?: OrNull<ISO8601Date>;
}

export interface IDirectUploadInputContent extends InputContentBase {
  attachable?: OrNull<FileAttachableType[]>;
  files?: OrNull<FileList>;
  multiple?: boolean;
  value?: OrNull<string[]>;
}

export interface IMultiSelectInputContent extends InputContentBase {
  options: ISelectionOption[];
  value?: OrNull<string[]>;
}

export interface INumericInputContent extends InputContentBase {
  precision: number;
  currency?: boolean;
  hint?: string;
  max?: number;
  min?: number;
  unit?: string;
  value?: OrNull<number>;
}

interface ISelectionOption {
  label: string;
  value: string;
  key?: OrNull<string>;
}

export interface ISingleSelectInputContent extends InputContentBase {
  format: SingleSelectInputFormatType;
  options: ISelectionOption[];
  element?: OrNull<string>;
  hint?: string;
  value?: OrNull<string>;
}

export interface ISliderInputContent extends InputContentBase {
  textEditable: boolean;
  ticks: ISliderTick[];
  characterMax?: OrNull<number>;
  increment?: OrNull<number>;
  leftTextLabel?: OrNull<string>;
  rightTextLabel?: OrNull<string>;
  maxErrorMessage?: string;
  minErrorMessage?: string;
  value?: OrNull<number>;
  valueBase?: OrNull<number>;
}

interface ISliderTick {
  label: string;
  value: number;
}

export interface ITaxIdInputContent extends InputContentBase {
  encryptOutput: boolean;
  hint: string;
  value?: string;
}

export interface ITextInputContent extends InputContentBase {
  format: TextInputFormatType;
  characterMax?: OrNull<number>;
  element?: string;
  hint?: string;
  value?: OrNull<string>;
}

export interface IToggleInputContent extends InputContentBase {
  text: string;
  value?: OrNull<boolean>;
}

export interface IUnitizedInputContent extends InputContentBase {
  options: IUnitizedOption[];
  value?: OrNull<(string | undefined)[]>;
}

interface IUnitizedOption {
  label: string;
  min: number;
  max: number;
}

/*
..................
~~~~~ UNION TYPES ~~~~~
..................
*/

export type DynamicFormCriteriaFlowScope =
  "coverage_adjustment" | "new_policy" | "policy_holder" | "reinstatement";
export type DynamicFormLayoutType = keyof typeof EnumDynamicFormLayouts;
export type DynamicFormItemType = keyof typeof EnumDynamicFormItemTypes;
type ArgumentSourceType = keyof typeof EnumArgumentSourceType;
type ArgumentType = keyof typeof EnumArgumentType;
type ButtonActionType = keyof typeof EnumButtonAction;
type ButtonStyleType = keyof typeof EnumButtonStyle;
type DecorationKeyType = keyof typeof EnumDecorationKey;
type FileAttachableType = keyof typeof EnumFileAttachable;
type FormGroupType = keyof typeof EnumFormGroup;
type GroupDetailComponentTypes = keyof typeof EnumGroupDetailComponent;
type SingleSelectInputFormatType = keyof typeof EnumSingleSelectInputFormat;
type SpacerModeType = keyof typeof EnumSpacerMode;
type SpacerValueType = keyof typeof EnumSpacerValue;
type ContentColorType = keyof typeof EnumContentColor;
type TextContentTypographyType = keyof typeof EnumTextTypography;
type TextInputFormatType = keyof typeof EnumTextInputFormat;
type TextContentStyleType = keyof typeof EnumTextContentStyle;

/*
..................
~~~~~ ENUMS ~~~~~
..................
*/

const EnumArgumentSourceType = {
  input: "input",
  response: "response",
  static: "static",
} as const;

const EnumArgumentType = {
  boolean: "boolean",
  float: "float",
  int: "int",
  string: "string",
} as const;

const EnumButtonAction = {
  api: "api", // local
  link: "link", // local/remote
  validation: "validation",
} as const;

const EnumButtonStyle = {
  back: "back",
  destructive: "destructive",
  exit: "exit",
  hidden: "hidden",
  primary: "primary",
  secondary: "secondary",
  textLink: "textLink",
} as const;

const EnumDecorationKey = {
  "bene-delete-confirm-image": "bene-delete-confirm-image",
  "privacy-insurance-application": "privacy-insurance-application",
  "terminal-illness-rider": "terminal-illness-rider",
  thin_divider: "thin_divider",
  "sms-transactional": "sms-transactional",
} as const;

const EnumDynamicFormItemTypes = {
  button: "button",
  decoration: "decoration",
  image: "image",
  inputCheckbox: "inputCheckbox",
  inputDate: "inputDate",
  inputDirectUpload: "inputDirectUpload",
  inputMultiSelect: "inputMultiSelect",
  inputNumeric: "inputNumeric",
  inputSingleSelect: "inputSingleSelect",
  inputSlider: "inputSlider",
  inputTaxId: "inputTaxId",
  inputText: "inputText",
  inputToggle: "inputToggle",
  inputUnitized: "inputUnitized",
  listRow: "listRow",
  space: "space",
  text: "text",
} as const;

export const EnumDynamicFormNonInputItemTypes = {
  button: "button",
  decoration: "decoration",
  image: "image",
  listRow: "listRow",
  space: "space",
  text: "text",
} as const;
export type EnumDynamicFormNonInputItemTypes =
  (typeof EnumDynamicFormNonInputItemTypes)[keyof typeof EnumDynamicFormNonInputItemTypes];

const EnumDynamicFormLayouts = {
  confirmation: "confirmation",
  modal: "modal",
  myBeneficiaries: "myBeneficiaries",
  purchasePolicy: "purchasePolicy",
  purchaseSuccess: "purchaseSuccess",
  quoteReview: "quoteReview",
  review: "review",
  terminal: "terminal",
  trivia: "trivia",
  vertical: "vertical",
} as const;

const EnumFileAttachable = {
  csv: "text/csv",
  html: "text/html",
  json: "application/json",
  jpeg: "image/jpeg",
  pdf: "application/pdf",
  png: "image/png",
  svg: "image/svg+xml",
  txt: "text/plain",
  xml: "application/xml",
} as const;

const EnumFormGroup = {
  api: "api",
  component: "component",
} as const;

const EnumGroupDetailComponent = {
  address: "address",
  container: "container",
  phone: "phone",
} as const;

export const EnumRetryArgumentName = {
  retryBackoffFactor: "retryBackoffFactor",
  retryDelay: "retryDelay",
  retryTimeout: "retryTimeout",
} as const;
export type EnumRetryArgumentName =
  (typeof EnumRetryArgumentName)[keyof typeof EnumRetryArgumentName];

const EnumSingleSelectInputFormat = {
  blobs: "blobs",
  dropdown: "dropdown",
  radio: "radio",
  rectangles: "rectangles",
} as const;

const EnumSpacerMode = {
  fixed: "fixed",
  flexible: "flexible",
} as const;

const EnumSpacerValue = {
  space0: "space0",
  space4: "space4",
  space8: "space8",
  space12: "space12",
  space16: "space16",
  space24: "space24",
  space32: "space32",
  space40: "space40",
  space48: "space48",
  space64: "space64",
  space80: "space80",
} as const;

const EnumContentColor = {
  ashes: "ashes",
  backgroundPrimary: "backgroundPrimary",
  backgroundPrimaryInverse: "backgroundPrimaryInverse",
  backgroundQuartenary: "backgroundQuartenary",
  backgroundSecondary: "backgroundSecondary",
  backgroundTertiary: "backgroundTertiary",
  black: "black",
  cherry: "cherry",
  coal: "coal",
  cobalt: "cobalt",
  eggplant: "eggplant",
  kaolin: "kaolin",
  leaf: "leaf",
  lemon: "lemon",
  lime: "lime",
  mamey: "mamey",
  orange: "orange",
  plaster: "plaster",
  platinum: "platinum",
  royal: "royal",
  sand: "sand",
  satin: "satin",
  scallion: "scallion",
  silver: "silver",
  sky: "sky",
  smoke: "smoke",
  textBold: "textBold",
  textError: "textError",
  textPrimary: "textPrimary",
  textPrimaryInverse: "textPrimaryInverse",
  textSecondary: "textSecondary",
  textTertiary: "textTertiary",
  watermelon: "watermelon",
  white: "white",
} as const;

const EnumTextInputFormat = {
  multiLine: "multiLine",
  singleLine: "singleLine",
} as const;

const EnumTextTypography = {
  d1: "d1",
  d2: "d2",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  b1: "b1",
  b2: "b2",
  b3: "b3",
  b4: "b4",
  b5: "b5",
  c1: "c1",
  c2: "c2",
  c3: "c3",
  c4: "c4",
  c5: "c5",
  l1: "l1",
  l2: "l2",
  l3: "l3",
  l4: "l4",
  l5: "l5",
  l6: "l6",
  n1: "n1",
  n2: "n2",
  n3: "n3",
  n4: "n4",
  n5: "n5",
  n6: "n6",
} as const;

const EnumTextContentStyle = {
  pill: "pill",
} as const;

/*
..................
~~~~~ PREDICATES ~~~~~
..................
*/

export function isDFGroupComponent(formData: DynamicFormGroup): formData is ComponentFormGroup {
  const formDataTypeIsComponent = formData.type === "component";

  return formDataTypeIsComponent;
}

export function isDFGroupApi(group: DynamicFormGroup): group is ApiFormGroup {
  return group.type === "api";
}

export function isInputCheckboxItem(item: DynamicFormItems): item is CheckboxInputFormItem {
  return item.type === "inputCheckbox";
}
